import { memo } from "react";
import { classNames } from "helpers";
import { useTranslate } from "helpers/common";
import { transformDateToLocalDateString } from "helpers/common";
import { NotificationCalendarItem } from "services/api/notifications/types";
import Badge from "ui/components/Badge/Badge";
import Button from "ui/components/Button/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import {
  CALENDAR_EVENT_COLORS,
  DEFAULT_CALENDAR_EVENT_COLOR,
} from "../../consts";
import { DayDetailsModalProps } from "../../types";

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex justify-between gap-4 text-sm py-1">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="font-medium text-gray-800 text-right min-w-0 truncate">{value}</span>
  </div>
);

const DayDetailsModal: React.FC<DayDetailsModalProps> = (props) => {
  const { show, date, items, onHide, onVehicleClick } = props;

  const { t } = useTranslate();

  return (
    <Modal show={show} onHide={onHide} size="small" position="centered">
      <ModalHeader
        title={`${t("admin.notification.calendar.details")} — ${
          date ? transformDateToLocalDateString(new Date(date)) : ""
        }`}
        onHide={onHide}
      />
      <ModalBody fixedFooterSpacing={false}>
        <div className="space-y-4 p-4">
          {items.map((item: NotificationCalendarItem, index: number) => {
            const color =
              CALENDAR_EVENT_COLORS[item.entityType] ?? DEFAULT_CALENDAR_EVENT_COLOR;

            const handleVehicleClick = () => {
              if (item.vehicle) onVehicleClick(item.vehicle.id);
            };

            // Obrub zaglavlja ide na samo zaglavlje, ne na karticu — inače bi gornji i
            // bočni rub dolazili od sivog obruba kartice.
            return (
              <div key={`${item.id}-${index}`} className="rounded-sm overflow-hidden">
                <div
                  className={classNames(
                    "flex items-center justify-between gap-3 px-4 py-3 border rounded-t-sm",
                    color.background,
                    color.borderStrong
                  )}
                >
                  <div className="min-w-0">
                    <p className={classNames("text-sm font-semibold", color.text)}>
                      {item.definitionTitle}
                    </p>
                    <p className={classNames("text-xs mt-0.5", color.subtext)}>
                      {t(`admin.notification.entityType.${item.entityType}`)}
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1 shrink-0">
                    {item.channels.map((channel) => (
                      <Badge key={channel} text={channel} variant="info" height="small" showDot />
                    ))}
                  </div>
                </div>

                {item.vehicle && (
                  <div className="px-4 py-3 border-x border-b border-slate-200">
                    <p className="text-xs uppercase font-semibold text-gray-400 mb-2">
                      {t("admin.notification.timeline.vehicle")}
                    </p>
                    <VehicleCell vehicle={item.vehicle} onClick={handleVehicleClick} />
                  </div>
                )}

                <div className="px-4 py-3 border-x border-b border-slate-200 rounded-b-sm">
                  {item.entityDisplayName && (
                    <DetailRow
                      label={t("admin.notification.calendar.detail.entity")}
                      value={item.entityDisplayName}
                    />
                  )}
                  {item.relevantDate && (
                    <DetailRow
                      label={t("admin.notification.calendar.detail.expiry")}
                      value={transformDateToLocalDateString(new Date(item.relevantDate))}
                    />
                  )}
                  {item.relevantDate && (
                    <DetailRow
                      label={t("admin.notification.calendar.detail.daysBefore")}
                      value={`${item.daysBeforeExpiry}`}
                    />
                  )}
                  <DetailRow
                    label={t("admin.notification.calendar.detail.sendCount")}
                    value={`${item.sendCount}`}
                  />
                  {item.repeatIntervalDays !== null && item.sendCount > 1 && (
                    <DetailRow
                      label={t("admin.notification.calendar.detail.repeatInterval")}
                      value={`${item.repeatIntervalDays}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-end px-4 py-3">
          <Button variant="outlined" onClick={onHide} size="small">
            {t("admin.notification.calendar.close")}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(DayDetailsModal);
